import { defineStore } from 'pinia';
import { computed, getCurrentInstance } from 'vue';
import { useToast } from 'vue-toast-notification';
import { useLoadingStore } from '@/pinia/loading.module';
import { useAuthStore } from '@/pinia/auth.module';
import { getLocaleMessage } from '@/services/MessagesService';
import { sharedStore } from '@/pinia/sharedStore.module';
import * as service from '@/services';

const $toast = useToast();

export const useAuthorizationBureauConsultationStore = defineStore('authorizationBureauConsultation', {
    persist: false,
    state: () => ({
        bureauConsultations: computed({
            get() { return sharedStore().bureauConsultations || [] },
            set(val) { sharedStore().bureauConsultations = val }
        }),
        errors: null,
        loanApplicationsOptions: []
    }),
    getters: {
        getBureauConsultations: state => state.bureauConsultations,
        getActiveBureauConsultations: state => state.bureauConsultations.filter(c => c.isActive),
        getConsultationById: state => id => state.bureauConsultations.find(c => c.id === id) || null,
        filterAuthorizationBureauConsultationBySearchString: state => (searchString = "") => {
            //#region control de fecha string
            const instance = getCurrentInstance();
            const $filters = instance?.appContext.config.globalProperties.$filters;
            //#endregion control de fecha string

            if (!searchString) return state.bureauConsultations.data;
            const lowerCaseSearchString = searchString.toLowerCase();

            return state.bureauConsultations.data.filter(bureau => {
                const code = bureau.referenceNumber ? bureau.referenceNumber.toLowerCase() : '';
                const codeLoanApplication = bureau.loanApplication.referenceNumber ? bureau.loanApplication.referenceNumber.toLowerCase() : '';
                const codeProspectus = bureau.loanApplication.prospectus?.referenceNumber ? bureau.loanApplication.prospectus?.referenceNumber.toLowerCase() : '';
                const placeIssuance = bureau.placeIssuace ? bureau.placeIssuace.toLowerCase() : '';
                const dateIssuance = bureau.dateIssuance ? $filters?.formatDate(bureau.dateIssuance).toLowerCase() : '';
                const dateSigned = bureau.dateSigned ? $filters?.formatDate(bureau.dateSigned).toLowerCase() : '';
                const nameOfficer = bureau.officerFullName ? bureau.officerFullName.toLowerCase() : '';
                const dateInquiry = bureau.inquiryDate ? $filters?.formatDate(bureau.inquiryDate).toLowerCase() : '';
                const firstNameProspect = bureau.loanApplication.prospectus.generalInformation.firstName ? bureau.loanApplication.prospectus.generalInformation.firstName.toLowerCase() : '';
                const lastNameProspect = bureau.loanApplication.prospectus.generalInformation.lastName ? bureau.loanApplication.prospectus.generalInformation.lastName.toLowerCase() : '';
                const mothersMaidenNameProspect = bureau.loanApplication.prospectus.generalInformation.mothersMaidenName ? bureau.loanApplication.prospectus.generalInformation.mothersMaidenName.toLowerCase() : '';
                const nameProspect = bureau.loanApplication.prospectus.generalInformation.businessName === null ?
                    `${firstNameProspect} ${lastNameProspect} ${mothersMaidenNameProspect}`
                    :
                    bureau.loanApplication.prospectus.generalInformation?.businessName ? bureau.loanApplication.prospectus.generalInformation?.businessName.toLowerCase() : '';
                const nameProspectusPersonType = bureau.loanApplication.prospectus?.personType?.name ? bureau.loanApplication.prospectus?.personType?.name.toLowerCase() : '';
                const status = bureau.status?.name ? bureau.status?.name.toLowerCase() : '';

                return code.includes(lowerCaseSearchString) ||
                    codeLoanApplication.includes(lowerCaseSearchString) ||
                    codeProspectus.includes(lowerCaseSearchString) ||
                    placeIssuance.includes(lowerCaseSearchString) ||
                    dateIssuance.includes(lowerCaseSearchString) ||
                    dateSigned.includes(lowerCaseSearchString) ||
                    nameOfficer.includes(lowerCaseSearchString) ||
                    dateInquiry.includes(lowerCaseSearchString) ||
                    nameProspect.includes(lowerCaseSearchString) ||
                    nameProspectusPersonType.includes(lowerCaseSearchString) ||
                    status.includes(lowerCaseSearchString);
            });
        }
    },
    actions: {
        setErrors(errors) {
            this.errors = errors;
        },
        /**
        * Consultar todos los registro existentes de Autorización de buró de crédito
        * http://localhost:5143/api/CreditBureauInquiries/all/${useAuthStore().user.companyId}
        */
        async fetchBureauConsultations() {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.bureauInquiriesApi.get(`/api/CreditBureauInquiries/all/${useAuthStore().user.companyId}`);
                if (data && data.status) {
                    this.bureauConsultations = data;
                }
                useLoadingStore().loading = false;
            } catch (error) {
                useLoadingStore().loading = false;
                this.setErrors(error);
                //Error al crear la autorizacion
                if (error.response) {
                    // Error de respuesta desde la API (status code no 2xx)
                    this.setErrors(error.response.data);
                    $toast.error(getLocaleMessage('common', 'responseError'));
                } else if (error.request) {
                    // Error de red, no se recibió respuesta
                    $toast.error(getLocaleMessage('common', 'networkError'));
                } else {
                    // Otro tipo de error
                    $toast.error(getLocaleMessage('common', 'generalError'));
                }
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Crear nuevo registro Autorización de buró de crédito
         * http://localhost:5143/api/CreditBureauInquiries
         */
        async createBureauConsultation(payload) {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.bureauInquiriesApi.post(`/api/CreditBureauInquiries/`, payload);
                if (data && data.status) {
                    $toast.success(getLocaleMessage('bureauInquiries', 'creatingSuccess'));
                    return data;
                }
            } catch (error) {
                useLoadingStore().loading = false;
                this.setErrors(error);
                //Error al crear la autorizacion
                if (error.response) {
                    // Error de respuesta desde la API (status code no 2xx)
                    this.setErrors(error.response.data);
                    $toast.error(getLocaleMessage('common', 'responseError'));
                } else if (error.request) {
                    // Error de red, no se recibió respuesta
                    $toast.error(getLocaleMessage('common', 'networkError'));
                } else {
                    // Otro tipo de error
                    $toast.error(getLocaleMessage('common', 'generalError'));
                }
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Actualizar un registro de Autorización de buró de crédito
         * http://localhost:5143/api/CreditBureauInquiries
         */
        async updateBureauConsultation(payload) {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.bureauInquiriesApi.put(`/api/CreditBureauInquiries`, payload);
                if (data && data.status) {
                    const index = this.bureauConsultations.data.findIndex(c => c.id === payload.id);
                    if (index !== -1) {
                        this.bureauConsultations.data.splice(index, 1, data.data);
                    }
                    $toast.success(getLocaleMessage('bureauInquiries', 'editingSuccess'));
                    return data.data;
                }
            } catch (error) {
                useLoadingStore().loading = false;
                this.setErrors(error);
                //Error al crear la autorizacion
                if (error.response) {
                    // Error de respuesta desde la API (status code no 2xx)
                    this.setErrors(error.response.data);
                    $toast.error(getLocaleMessage('common', 'responseError'));
                } else if (error.request) {
                    // Error de red, no se recibió respuesta
                    $toast.error(getLocaleMessage('common', 'networkError'));
                } else {
                    // Otro tipo de error
                    $toast.error(getLocaleMessage('common', 'generalError'));
                }
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Opciones de solicitud para llenar el combo de selección de solicitud para los documentos
         * http://localhost:5143/api/CreditBureauInquiries/filterLoanApplications/1 ${userAuth.user.companyId}
         */
        async filterBureauLoanApplications() {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.bureauInquiriesApi.get(`/api/CreditBureauInquiries/filterLoanApplications/${useAuthStore().user.companyId}`);
                if (data && data.status) {
                    this.loanApplicationsOptions = data.data;
                }
                useLoadingStore().loading = false;
            } catch (error) {
                useLoadingStore().loading = false;
                this.setErrors(error);
                //Error al crear la autorizacion
                if (error.response) {
                    // Error de respuesta desde la API (status code no 2xx)
                    this.setErrors(error.response.data);
                    $toast.error(getLocaleMessage('common', 'responseError'));
                } else if (error.request) {
                    // Error de red, no se recibió respuesta
                    $toast.error(getLocaleMessage('common', 'networkError'));
                } else {
                    // Otro tipo de error
                    $toast.error(getLocaleMessage('common', 'generalError'));
                }
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        }
    }
});